import { Form, Input } from 'antd'
import { useTranslation } from 'react-i18next'

import { ButtonRadio } from '@/components/base/ButtonRadio'
import { NumberInput } from '@/components/base/NumberInput'
import { isValidMaxReplicas, parseRequestCount } from '@/pages/auto-deploy/scalingPolicy'

function ScalingModeInput({
  value = 0,
  onChange,
  disabled,
}: {
  value?: 0 | 1
  onChange?: (value: 0 | 1) => void
  disabled?: boolean
}) {
  const { t } = useTranslation('autoDeploy')
  return (
    <ButtonRadio
      value={value === 1 ? 'auto' : 'manual'}
      onChange={(mode) => onChange?.(mode === 'auto' ? 1 : 0)}
      disabled={disabled}
      rootProps={{ role: 'radiogroup', 'aria-label': t('scalingPolicy.mode') }}
      unselectedClassName="bg-panel-gray-100"
      items={[
        {
          label: t('scalingPolicy.manual'),
          value: 'manual',
          buttonProps: { role: 'radio', 'aria-checked': value === 0 },
        },
        {
          label: t('scalingPolicy.auto'),
          value: 'auto',
          buttonProps: { role: 'radio', 'aria-checked': value === 1 },
        },
      ]}
    />
  )
}

function RequestCountInput({
  hint,
  ...props
}: React.ComponentProps<typeof Input> & { hint: string }) {
  const { errors } = Form.Item.useStatus()
  const { t } = useTranslation('autoDeploy')
  return (
    <>
      <Input
        {...props}
        inputMode="numeric"
        className="h-11 px-4 t-sb border-neutral-200"
        suffix={<span className="t-sb text-primary">{t('scalingPolicy.requests')}</span>}
      />
      {errors.length === 0 && <p className="t-sm text-secondary mt-2">{hint}</p>}
    </>
  )
}

export function ScalingPolicyFields({
  disabled = false,
  savedMaximum,
  compact = false,
}: {
  disabled?: boolean
  savedMaximum?: number
  compact?: boolean
}) {
  const { t } = useTranslation('autoDeploy')
  const form = Form.useFormInstance()
  const mode = Form.useWatch('scalingMode', form) ?? 0
  return (
    <div
      className={`flex flex-col [&_.ant-form-item-label>label]:!h-5 ${compact ? 'gap-6' : 'gap-8'}`}
    >
      <div>
        <Form.Item
          name="scalingMode"
          label={<span className="t-sb">{t('scalingPolicy.mode')}</span>}
          className="mb-0"
        >
          <ScalingModeInput disabled={disabled} />
        </Form.Item>
        <p className="t-sm text-secondary mt-2">
          {t(mode === 1 ? 'scalingPolicy.autoHint' : 'scalingPolicy.manualHint')}
        </p>
      </div>
      <div>
        <Form.Item
          name="maxReplicas"
          label={<span className="t-sb">{t('createSubPage.form.maxNumberIns')}</span>}
          className="mb-0"
          rules={[
            {
              validator: (_, value) =>
                isValidMaxReplicas(value, savedMaximum)
                  ? Promise.resolve()
                  : Promise.reject(new Error(t('createSubPage.form.maxReplicasHint'))),
            },
          ]}
        >
          <NumberInput
            value={1}
            className="w-[264px] max-w-full"
            min={1}
            max={Math.max(10, savedMaximum ?? 10)}
            disabled={disabled}
            inputProps={{ 'aria-label': t('createSubPage.form.maxNumberIns') }}
          />
        </Form.Item>
        <p className="t-sm text-secondary mt-2">* {t('createSubPage.form.maxReplicasHint')}</p>
      </div>
      {mode === 1 &&
        (
          [
            ['concurrencyPerReplica', 'concurrency', 1],
            ['maxPendingRequests', 'pending', 0],
          ] as const
        ).map(([name, key, minimum]) => (
          <Form.Item
            key={name}
            name={name}
            label={<span className="t-sb text-with-star">{t(`scalingPolicy.${key}`)}</span>}
            className="mb-0 w-full max-w-[576px]"
            rules={[
              {
                validator: (_, value) =>
                  parseRequestCount(value, minimum) !== undefined
                    ? Promise.resolve()
                    : Promise.reject(new Error(t(`scalingPolicy.${key}Error`))),
              },
            ]}
          >
            <RequestCountInput
              aria-label={t(`scalingPolicy.${key}`)}
              placeholder={key === 'concurrency' ? '4' : '20'}
              disabled={disabled}
              hint={t(`scalingPolicy.${key}Hint`)}
            />
          </Form.Item>
        ))}
    </div>
  )
}
